import { useEffect, useState, useRef } from 'react'
import { CheckIcon, CircleNotchIcon, CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react'
import { ScoopText, ScoopRow, ScoopColumn, ScoopButton } from '../../../../ui'
import styles from './ProcessingSteps.module.scss'

export const ProcessingSteps = ({ steps, isComplete, showSteps = true }) => {
    const [elapsedTime, setElapsedTime] = useState({})
    const [isCollapsed, setIsCollapsed] = useState(false)
    const intervalRef = useRef(null)

    useEffect(() => {
        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }

        // Start tracking time if not complete
        if (!isComplete && steps.length > 0) {
            intervalRef.current = setInterval(() => {
                const updated = {}
                steps.forEach((step) => {
                    if (step.startTime && !step.endTime) {
                        const elapsed = Date.now() - step.startTime
                        updated[step.id] = elapsed
                    } else if (step.endTime && step.startTime) {
                        updated[step.id] = step.endTime - step.startTime
                    }
                })
                setElapsedTime(updated)
            }, 100) // Update every 100ms for smooth time display
        }

        // Cleanup on unmount or when complete
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [steps, isComplete])

    const formatTime = (ms) => {
        if (!ms) return '0.0s'
        if (ms < 1000) return `${(ms / 1000).toFixed(1)}s`
        return `${(ms / 1000).toFixed(1)}s`
    }

    const getStepTime = (step) => {
        if (step.endTime && step.startTime) {
            return formatTime(step.endTime - step.startTime)
        }
        if (step.startTime) {
            return formatTime(elapsedTime[step.id] || 0)
        }
        return null
    }

    const getStepIcon = (step) => {
        if (step.endTime) return <CheckIcon size={14} color={styles.colorSuccess} weight="bold" />
        if (step.startTime && !step.endTime)
            return <CircleNotchIcon size={14} color={styles.colorPrimary} className="spinning" />
        return <div className={styles.iconPlaceholder} />
    }

    if (steps.length === 0) return null

    // If showSteps is false and not complete, show minimal current step
    if (!showSteps && !isComplete) {
        const currentStep = steps[steps.length - 1]
        return (
            <ScoopRow className={styles.minimalContainer}>
                {currentStep && !currentStep.endTime && (
                    <CircleNotchIcon size={14} color={styles.colorPrimary} className={'spinning'} />
                )}
                <ScoopText className={styles.minimalText}>
                    {currentStep?.message || 'Processing...'}
                </ScoopText>
            </ScoopRow>
        )
    }

    // Don't show anything for completed messages when toggle is off
    if (!showSteps && isComplete) return null

    const totalTime =
        steps.length > 0 && steps[0].startTime
            ? formatTime((steps[steps.length - 1]?.endTime || Date.now()) - steps[0].startTime)
            : '0.0s'

    return (
        <div className={styles.container}>
            <ScoopRow className={`${styles.header} ${isCollapsed ? styles.headerCollapsed : ''}`}>
                <ScoopRow className={styles.headerLeft}>
                    {isComplete && (
                        <ScoopButton
                            variant="text"
                            size="xxs"
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className={styles.collapseButton}
                        >
                            {isCollapsed ? (
                                <CaretRightIcon size={12} />
                            ) : (
                                <CaretDownIcon size={12} />
                            )}
                        </ScoopButton>
                    )}
                    <ScoopText className={styles.headerTitle}>Processing Steps</ScoopText>
                </ScoopRow>
                {isCollapsed && (
                    <ScoopText className={styles.totalTime}>Total: {totalTime}</ScoopText>
                )}
            </ScoopRow>

            <div className={`${styles.collapseContainer} ${isCollapsed ? styles.collapsed : ''}`}>
                <ScoopColumn className={styles.stepsList}>
                    {steps.map((step, index) => (
                        <ScoopRow
                            key={step.id}
                            className={`${styles.stepItem} ${step.startTime ? styles.active : styles.pending}`}
                        >
                            {/* Step icon */}
                            <div className={styles.stepIcon}>{getStepIcon(step)}</div>

                            {/* Step message */}
                            <ScoopText
                                className={`${styles.stepMessage} ${step.endTime ? styles.completed : styles.active}`}
                            >
                                {step.message}
                            </ScoopText>

                            {/* Elapsed time */}
                            {step.startTime && (
                                <ScoopText
                                    className={`${styles.stepTime} ${step.endTime ? styles.completed : styles.active}`}
                                >
                                    {getStepTime(step)}
                                </ScoopText>
                            )}
                        </ScoopRow>
                    ))}
                </ScoopColumn>

                {/* Total time if complete */}
                {isComplete && steps.length > 0 && steps[0].startTime && (
                    <ScoopRow className={styles.totalFooter}>
                        <ScoopText className={styles.totalLabel}>Total time:</ScoopText>
                        <ScoopText className={styles.totalValue}>
                            {formatTime(
                                (steps[steps.length - 1]?.endTime || Date.now()) -
                                    steps[0].startTime
                            )}
                        </ScoopText>
                    </ScoopRow>
                )}
            </div>
        </div>
    )
}
